import React, { useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { IntlProvider } from 'react-intl';
import { i18nConfig } from '../assets';
import { defaultArticle, defaultShopSettings } from './AppState';
import sessionService from '../services/SessionService';
import storageService from '../services/StorageService';
import shopSettingsService from '../services/ShopSettingsService';
import RenderUtil from '../util/RenderUtil';
import DefaultTheme from '../theme/DefaultTheme';
import {
  ArticleData,
  ArticleResource,
  CurrencyISO4217,
  MEASUREMENT_END,
  CONVERSION_ACTION,
  FOOT_ARCH_OPTION,
  FUNNEL,
  FrameSource,
  Matching,
  MultiRecommendation,
  Recommendation,
  ShopSettings,
} from '../interfaces/ProductService.interface';
import { SPEED_OPTION } from '../components/InputSelection/interface';
import { CtaPanelEvents } from '../components/CtaPanel';
import shopService from '../services/ShopService';
import checkoutService from '../services/CheckoutService';
import { Events } from '@footprint/common-components';
import { PollingEvents } from '../components/QRGateway/Interface';
import { RecommendationEvents } from '../components/Recommendation';
import { Modal, ModalView, ModalEvents } from '../components/Common/Modal';
import SplashscreenEvents from '../components/Splashscreen/Events';
import { PollingState } from '../components/QRGateway';
import { DefaultError } from '../error/DefaultError';
import clientEventsService from '../services/ClientEventsService';
import WidgetEvents from './Events';
import { AssetsService } from '../services/AssetsService';
import log from '../services/LogService';
import FaEvents from '../components/InputSelection/Events';
import measurementService from '../services/MeasurementService';
import McEvents from '../components/McGateway/Events';
import { EmailFormEvents } from '../components/EmailForm';

export const ShopSettingsContext = React.createContext<ShopSettings>(defaultShopSettings);
export const ArticleContext = React.createContext<ArticleData>(defaultArticle);

export const AppController = (props: any) => {
  const { articleList, emitter, bannerId, headlessEnabled } = props;
  log.info('initializing appcontext');
  let currentArticle: ArticleData = null;
  let customerReference: string = null;

  const initLang = RenderUtil.getLang();
  const initMessages = RenderUtil.getTranslationMessages(initLang, i18nConfig);

  const [ shopSettings, setShopSettings] = useState(defaultShopSettings as ShopSettings);
  const [ article, setArticle ] = useState(defaultArticle as ArticleData);
  const [ locale, setLocale ] = useState(i18nConfig[initLang].locale);
  const [ messages, setMessages ] = useState(initMessages);
  const [ customTheme, setCustomTheme ] = useState(DefaultTheme);
  const [ language, setLanguage ] = useState(initLang);
  const [ modalView, setModalView ] = useState(ModalView.UNDEFINED);
  const [ modalData, setModalData ] = useState({} as any);

  let sessionId = storageService.getSessionId();

  if (sessionId) {
    shopService.updateSessionId(sessionId);
  }

  const clearModal = () => {
    RenderUtil.closeModal();
    setModalData({});
    setModalView(ModalView.UNDEFINED);
  };

  const handleTimeout = () => {
    sessionService.updateExitCode('PDP', 'qr.timeout');
    clientEventsService.createEvent('shop.qr.timeout');
  };

  const resetMeasurement = () => {
    storageService.clearMatchings();
    storageService.clearMeasurement();
  };

  const displaySplashscreen = () => {
    const funnel = shopSettings.funnel || FUNNEL.UNDEFINED;
    setModalData({ funnel });
    setModalView(ModalView.SPLASHSCREEN);
    RenderUtil.openModal();
  };

  const displayInputSelection = () => {
    log.info('Displaying Input Selection');
    const funnel = shopSettings.funnel || FUNNEL.UNDEFINED;
    setModalData({ funnel });
    setModalView(ModalView.INPUT_SELECTION);
    RenderUtil.openModal();
  };

  const displayEmbeddedMeasurementScreen = (fptAppUrl: string) => {
    setModalData({ src: fptAppUrl });
    setModalView(ModalView.MEASUREMENT);
    RenderUtil.openModal();
  };

  const displayRecommendation = async (matching: Matching) => {
    log.info('displaying recommendation');
    const recommendationPriceEnabled = shopSettings.recommendationPriceEnabled
      !== false;
    const recommendationMatchingCodeEnabled =
      !!shopSettings.recommendationMatchingCodeEnabled;

    const { data } = await measurementService.getMeasurementFrames();
    const hasPiFrames = !!data.find(frame => frame.source === FrameSource.PI);

    setModalData({
      matching,
      recommendationPriceEnabled,
      recommendationMatchingCodeEnabled,
      hasPiFrames,
    });
    setModalView(ModalView.RECOMMENDATION);
    RenderUtil.openModal();
  };

  const displayMultiRecommendation = (recommendation: MultiRecommendation) => {
    log.info('displaying multi recommendation');
    setModalData({ recommendation });
    setModalView(ModalView.MULTI_RECOMMENDATION);
    RenderUtil.openModal();
  };

  const displayEmailForm = () => {
    log.info('Displaying Email Form');
    setModalData({});
    setModalView(ModalView.EMAIL_FORM);
    RenderUtil.openModal();
  };

  const displaySuccessOutcome = () => {
    log.info('Displaying Email Form');
    setModalData({});
    setModalView(ModalView.SUCCESS_OUTCOME);
    RenderUtil.openModal();
  };

  const displayError = (err: Error) => {
    if (!(err instanceof DefaultError)) {
      err = new DefaultError(
        'GEN01-00',
        'Something went wrong',
        { cause:err }
      );
    }
    setModalData({ error: err });
    setModalView(ModalView.ERROR);
    RenderUtil.openModal();
    shopService.logError(err as DefaultError, currentArticle);
  };

  const displayDesktopWidget = () => {

    sessionService.updateExitCode('PDP', 'qr.displayed');
    clientEventsService.createEvent('shop.qr.displayed')
      .then(() => {
        const target = window.location.origin + window.location.pathname; // process.env.FPT_APP_URL;
        const data = shopService.getQRData();
        const params = new URLSearchParams(window.location.search);
        params.append('fpt-article-number', currentArticle.number);
        params.append('fpt-session', data.session);
        params.append('fpt-flow', data.flow);

        if (data.theme) {
          params.append('fpt-theme', data.theme);
        }

        const flowParts = data.flow.split('-');
        const lastPart = flowParts.length - 1;

        const pollingEnabled = flowParts[lastPart] == MEASUREMENT_END.EXT;
        log.info(`opening QRGateway: ${target}?${params.toString()}`);
        const url = new URL(`${target}?${params.toString()}`);
        setModalData({ target:url, polling: PollingState.UNDEFINED });
        setModalView(ModalView.QR);
        RenderUtil.openModal();

        if (pollingEnabled) {
          return shopService.waitForUserToStartMeasuring()
            .then(outcome => {
              setModalData({ target:url, polling: outcome });
              return shopService.waitForUserToFinishMeasuring();
            });
        }

        return PollingState.UNDEFINED;
      })
      .then((outcome: PollingState) => {

        if (outcome === PollingState.UNDEFINED) {
          // Nothing to do since polling was not enabled
          return;
        }

        log.info('finished measuring');

        shopService.handleMeasurementDone(currentArticle)
          .then((result:Matching) => {
            emitter.emit(WidgetEvents.onMatching, result);
            const event = new CustomEvent("fpt-matching", { detail: result });
            window.dispatchEvent(event);

            if (shopService.isSuccessfulMatching(result)) {
              displayRecommendation(result);
              shopService.createConversion(
                result,
                currentArticle,
                CONVERSION_ACTION.MATCHING_PRIMARY,
              );
              return;
            }

            throw new DefaultError(result.error_code, 'Matching failed');
          })
          .catch(err => {
            displayError(err);
          });
      })
      .catch(outcome => {

        if (outcome === PollingState.CANCELLED) {
          log.info('polling cancelled');
          clearModal();
          return;
        }

        if (outcome === PollingState.TIMEOUT) {
          log.info('polling timeout');
          handleTimeout();
          clearModal();
          return;
        }

        displayError(outcome);

      });

  };

  const displayStartScreen = () => {
    const isFootArchEnabled = RenderUtil.isFootArchEnabled(shopSettings);
    console.log('is Mobile:', RenderUtil.isMobile());
    if (
      shopSettings.desktopFlowEnabled &&
      !RenderUtil.isMobile()
    )
    {
      displayDesktopWidget();
    } else if (isFootArchEnabled) {
      displayInputSelection();
    } else {
      displaySplashscreen();
    }
  };

  const handleFptStart = (data:any) => {
    log.info('fpt start called with payload', data);

    if (data && data.detail) {
      customerReference = data.detail.customer_reference;
      const loginReference = data.detail.login_reference || null;
      shopService.setLoginReference(loginReference);
      clientEventsService.setExternalReference(loginReference);
    }

    if (headlessEnabled) {
      currentArticle = RenderUtil.getArticleDataFromModal();
      const price = currentArticle.price;
      const priceNum = price ? RenderUtil.parseArticlePrice(price, shopSettings) : null;
      const currency = currentArticle.currency || shopSettings.currency || CurrencyISO4217.EUR;
      currentArticle.sum = price ? checkoutService.formatCurrency(
        priceNum, locale, currency) : null;
      log.info('Headless enabled,setting article data', currentArticle);
    }

    if (data && data.detail && data.detail.articleNumber) {
      currentArticle.number = data.detail.articleNumber;
    }

    const params = new URLSearchParams(window.location.search);
    let onArticleCheck:Promise<ArticleResource>;

    if (params.has('fpt-session')) {
      onArticleCheck = shopService.checkIfArticleExists(currentArticle);
    } else {
      onArticleCheck = shopService.createSession()
        .then(result => {
          shopService.updateSessionId(result);
          return shopService.checkIfArticleExists(currentArticle);
        });
    }

    onArticleCheck
      .then(result => {
        currentArticle.number = result.article_number;
        setArticle(currentArticle);
        displayStartScreen();
      })
      .catch(error => {

        if (error.errorCode === 'WDAR1' && shopSettings.hasDefaultArticle) {
          return displayStartScreen();
        }

        displayError(error);
        let code = 'GEN01-00';

        if (error instanceof DefaultError) {
          code = error.errorCode;
        }

        sessionService.updateExitCode('error', code);
        log.error(`something went wrong: ${error}`);
      });
  };

  const processArticleDataAndDisplaySplashscreen = (result:ArticleResource) => {

    const OptionalPlantarImagePat = /.*-(PIOPT)/;
    const MandatoryPlantarImagePat = /.*-PI$/;

    if (currentArticle.gtin || currentArticle.ean || currentArticle.sku) {
      log.info(`setting article number ${result.article_number}`);
      currentArticle.number = result.article_number;
      setArticle(currentArticle);
    }

    if (shopSettings.insoleAvailable && RenderUtil.isArticleInsole(result)) {
      const { steps } = shopSettings;
      shopSettings.steps = OptionalPlantarImagePat.test(steps) ?
        steps.replace('PIOPT', 'PI') : MandatoryPlantarImagePat.test(steps) ?
        steps: `${steps}-PI`;
      setShopSettings(shopSettings);
      shopService.setShopSettings(shopSettings);
    }

    displayStartScreen();
  };

  const handleProcessArticleDataError = error => {

    if (error.errorCode === 'WDAR1' && shopSettings.hasDefaultArticle) {
      return displayStartScreen();
    }

    displayError(error);
    let code = 'GEN01-00';

    if (error instanceof DefaultError) {
      code = error.errorCode;
    }

    sessionService.updateExitCode('error', code);
    log.error(`something went wrong: ${error.message}`);
  };

  const handleCtaButtonPress = (articleData: ArticleData) => {

    log.info(`CTA Button clicked ${articleData.number}`, articleData);
    setArticle(articleData);
    currentArticle = articleData;

    setModalView(ModalView.PROCESSING);
    RenderUtil.openModal();

    shopService.handleCtaButtonClick(articleData)
      .then(processArticleDataAndDisplaySplashscreen)
      .catch(handleProcessArticleDataError);

  };

  const handleSingleMatch = (matching:Matching) => {
    emitter.emit(WidgetEvents.onMatching, matching);
    const event = new CustomEvent("fpt-matching", { detail: matching });
    window.dispatchEvent(event);
    const { measurement_id } = matching;
    measurementService.setMeasurementId(measurement_id);

    if (shopService.isSuccessfulMatching(matching)) {
      displayRecommendation(matching);
      shopService.createConversion(
        matching,
        currentArticle,
        CONVERSION_ACTION.MATCHING_PRIMARY,
      );
      return;
    }

    throw new DefaultError(matching.error_code, 'Matching failed');
  }

  const prepareNewMeasurement = () => {
    clearModal();
    resetMeasurement();
  };

  const handleMeasurementProcessError = (err) => {
    const errData = { code: err.errorCode, message: err.message };
    const event = new CustomEvent("fpt-error", { detail: errData });
    window.dispatchEvent(event);

    if (err instanceof DefaultError && err.errorCode === 'WDCNV1') {
      log.warn('App closed by the user');
      return;
    }

    log.error(`something went wrong ${err.message}`);
    displayError(err);
  };

  const handleMatchingResult = (result:Matching|MultiRecommendation, additionalParams?:string) => {

    if (!result) {
      return;
    }

    const returnUrl = currentArticle?.returnUrl || shopSettings.returnUrl;

    if (returnUrl && result.error_code && result.error_code === "MAST0-00") {
      return shopService.createConversion(
        result as Matching,
        currentArticle,
        CONVERSION_ACTION.MATCHING_PRIMARY,
        customerReference)
        .then(() => {
          window.location.href = shopService
            .parseReturnUrlWithRecommendation(returnUrl, result as Matching, additionalParams);
        });
    }

    if (headlessEnabled && result.variants) {
      if (additionalParams) {
        const p = new URLSearchParams(additionalParams);
        p.forEach((value, key) => result[key] = value);
      }
      return shopService.createRecommendationConversions(
        result as MultiRecommendation, customerReference)
        .then(() => {
          const recommendationEvent = new CustomEvent(
            'fpt-recommendation', { detail: result });
          window.dispatchEvent(recommendationEvent);
        })
        .catch((err) => {
          clientEventsService.createEvent(
            'shop.multi-recommendation.conversion-error', err.toString())
          .then(() => {
            const recommendationEvent = new CustomEvent(
              'fpt-recommendation', { detail: result });
            window.dispatchEvent(recommendationEvent);
          });
        });
    }

    if (result.variants) {
      return displayMultiRecommendation(result as MultiRecommendation);
    }

    handleSingleMatch(result as Matching);
  }

  const handleFunnelSelect = (funnel: FUNNEL) => {
    log.info(`${funnel} funnel selected`);
    const option = funnel.toLowerCase();
    clientEventsService.createEvent(`shop.splashscreen.${option}-press`);

    prepareNewMeasurement();

    const returnUrl = currentArticle?.returnUrl || shopSettings.returnUrl;

    let waitForResult: Promise<any>;
    const isEmbeddedMode = RenderUtil.isEmbeddedMode();

    if(isEmbeddedMode) {
      log.info(`handling funnel select in embeded mode ${funnel}`);
      const fptAppUrl = shopService.getFptAppUrl(funnel, currentArticle);
      log.info(`[AppController] opening conversion at ${fptAppUrl}`);
      displayEmbeddedMeasurementScreen(fptAppUrl);
      waitForResult = shopService.handleFunnelSelectEmbeddedMode()
      .then((data) => {
        clearModal();
        return data;
      });
    } else {
      waitForResult = shopService.handleFunnelSelect(funnel, currentArticle);
    }

    waitForResult
      .then((data) => {
        if (returnUrl && shopSettings.recommendationEnabled == false && data.result && data.result.measurementId) {
          window.location.href = shopService.parseReturnUrl(returnUrl,data.result,currentArticle, customerReference);
          return;
        }
        const event = new CustomEvent("fpt-measurement", { detail: data ? data.result : null });
        window.dispatchEvent(event);
        return shopService.handleMeasuringResult(data, currentArticle);
      })
      .then(handleMatchingResult)
      .catch(handleMeasurementProcessError);
  };

  const getInterstepsForFootArchOptions = () => {
    shopSettings.steps = shopSettings.steps.replace('-PIOPT', '');
    const match = /(.*)-FA$/.exec(shopSettings.steps);
    if (!match) {
      throw new Error(
        `Invalid steps configuration for Foot Arch flow: ${shopSettings.steps}`);
    }
    const interSteps = match[1];
    const updated = Object.assign({}, shopSettings);
    updated.steps = interSteps;
    setShopSettings(updated);
    return interSteps;
  }

  const displayMcGateway = (data) => {
    setModalData(data);
    setModalView(ModalView.MC_GATEWAY);
    RenderUtil.openModal();
  };

  const handleMcOptionSelect = () => {
    prepareNewMeasurement();

    // setModalData({ sessionId: 'test', measurementId: 'test' });
    // setModalView(ModalView.MC_GATEWAY);
    // RenderUtil.openModal();

    let waitForResult: Promise<any> = Promise.resolve();
    const funnel = FUNNEL.OTHER;
    const isEmbeddedMode = RenderUtil.isEmbeddedMode();

    if(isEmbeddedMode) {
      log.info(`handling funnel select in embeded mode ${funnel}`);
      const fptAppUrl = shopService.getFptAppUrl(funnel, currentArticle);
      displayEmbeddedMeasurementScreen(fptAppUrl);
      waitForResult = shopService.handleFunnelSelectEmbeddedMode();
    } else {
      waitForResult = shopService.openFptAppForFunnel(funnel, currentArticle);
    }

    waitForResult.then(data => {
      log.info(`displaying MC Gateway . . .`);
      if (!data.result || !data.result.measurementId) {
        throw new Error('Fpt App returned with no measurement');
      }
      const { sessionId, measurementId } = data.result;
      shopService.updateSessionId(sessionId);
      measurementService.setMeasurementId(measurementId);
      displayMcGateway({ sessionId, measurementId });
    });
  };

  const handleSpeedOptionConfirm = (selectedOption:SPEED_OPTION ) => {
    log.info('Speed Option confirmed:', selectedOption);
    const { steps } = shopSettings;
    if (selectedOption === SPEED_OPTION.FAST_TRACK) {
      shopSettings.steps = steps.replace(/^(\w+)/, 'POWER');
      setShopSettings(shopSettings);
      shopService.setShopSettings(shopSettings);
    }
    const OPTION_MAP = {};
    OPTION_MAP[SPEED_OPTION.FAST_TRACK] = 'Fast Track mode';
    OPTION_MAP[SPEED_OPTION.STANDARD] = 'Standard mode';
    clientEventsService.createEvent('shop.input-selection.select', OPTION_MAP[selectedOption]);
  }

  const handleMeasureOptionConfirm = (selectedOption: FOOT_ARCH_OPTION) => {
    const { steps } = shopSettings;
    log.info('Hanlde Foot Arch Option Select', selectedOption);
    const OPTION_MAP = {};
    OPTION_MAP[FOOT_ARCH_OPTION.MC] = 'minipod';
    OPTION_MAP[FOOT_ARCH_OPTION.PI] = 'plantar image';
    OPTION_MAP[FOOT_ARCH_OPTION.FPT] = 'mobile arch';
    OPTION_MAP[FOOT_ARCH_OPTION.NONE] = 'LP foot only';
    clientEventsService.createEvent('shop.input-selection.select', OPTION_MAP[selectedOption]);

    if (
      selectedOption === FOOT_ARCH_OPTION.MC ||
      selectedOption === FOOT_ARCH_OPTION.PI ||
      selectedOption === FOOT_ARCH_OPTION.NONE
    ) {
      shopSettings.steps = getInterstepsForFootArchOptions();
    }

    if (
      selectedOption === FOOT_ARCH_OPTION.PI
    ) {
      shopSettings.steps = `${shopSettings.steps}-PI`;
    }

    if (
      selectedOption === FOOT_ARCH_OPTION.FPT
    ) {
      shopSettings.steps = steps.replace('-PIOPT', '');
    }

    setShopSettings(shopSettings);
    shopService.setShopSettings(shopSettings);

    if (
      selectedOption === FOOT_ARCH_OPTION.MC
    ) {
      return handleMcOptionSelect();
    }

    return handleFunnelSelect(FUNNEL.OTHER);
  };

  const handleMcPollingStart = (params) => {
    log.info(`Polling started with params`, params);
    const sparams = new URLSearchParams(params);
    const mtype = sparams.get('hasOrthopedicsOrPain') ? 'dynamic' : 'static';
    clientEventsService.createEvent(
      'shop.mc-gateway.polling-start', mtype);
  }

  const handleMcPollingDone = (outcome) => {
    const { frame, params } = outcome;
    const sparams = new URLSearchParams(params);
    const mtype = sparams.get('hasOrthopedicsOrPain') ? 'dynamic' : 'static';
    clientEventsService.createEvent(
      'shop.mc-gateway.polling-done', `${frame.arch_type}, ${mtype}`);
    shopService.processMeasurement(
      frame.measurement_id, currentArticle)
      .then((matching) => handleMatchingResult(matching, params))
      .catch(handleMeasurementProcessError);
  }

  const handleProfileMeasurementSet = (data) => {
    const { articleNumber, measurementId } = data;
    log.info(`Handling profile measurement set for measurement ${measurementId}`);
    const articleData = articleList.find(
        item => item.number === articleNumber);
    shopService.processMeasurement(
      measurementId, articleData)
      .then((matching:Matching) => {
        emitter.emit(WidgetEvents.onMatching, matching);
        const event = new CustomEvent("fpt-matching", { detail: matching });
        window.dispatchEvent(event);
      })
      .catch(handleMeasurementProcessError);
  };

  const handleMcPollingCancelled = () => {
    clientEventsService.createEvent(
      'shop.mc-gateway.polling-cancelled');
    log.info('Polling cancelled');
    prepareNewMeasurement();
    displayInputSelection();
  };

  const handleMcPollingError = (err) => {
    const event = new CustomEvent("fpt-error", { detail: err });
    window.dispatchEvent(event);
    log.error(`something went wrong ${err.message}`);
    displayError(err);
    clientEventsService.createEvent(
      'shop.mc-gateway.polling-error', err.message);
  };

  const handleLanguageSetting = (selectedLanguage: string, settings:ShopSettings = shopSettings) => {
    log.info(`Handling Language ${selectedLanguage} selected`);
    setLanguage(selectedLanguage);
    const { lang } = settings;

    if (lang !== selectedLanguage) {
      settings.lang = selectedLanguage;
      // setShopSettings(shopSettings);
    }

    const langOptions = {
      lang: selectedLanguage,
      i18nNamespace: settings.theme?.i18nNamespace,
    };

    log.info(`handleLanguageSetting using i18nNamespace ${langOptions.i18nNamespace}`);

    return AssetsService.fetchLanguageFile(langOptions)
      .then( result => {
        log.info(`setting language ${selectedLanguage}`);
        i18nConfig[selectedLanguage].messages = result;
        setLocale(i18nConfig[selectedLanguage].locale);
        const m = RenderUtil.getTranslationMessages(selectedLanguage, i18nConfig);
        setMessages(m);
      });
  }

  const handleLanguageSelect = (selectedLanguage: string) => {
    handleLanguageSetting(selectedLanguage);
    clientEventsService.createEvent('shop.splashscreen.language-select', selectedLanguage);
  }

  const handleRecommendationPress = (data) => {
    log.info(`Recommendation pressedS`, data);
    setArticle(data.article);
    currentArticle = data.article;
    const { matching } = data;

    if (matching.error_code === 'MAST0-00') {
      const { measurement_id } = matching;
      measurementService.setMeasurementId(measurement_id);
      return displayRecommendation(matching);
    }

    sessionService.updateExitCode('error', matching.error_code);

    // let errorCode = matching.error_code;
    //
    // if (matching.error_code == 'BEDB1-00') {
    //   errorCode = 'BEDB1';
    // }

    displayError( new DefaultError(matching.error_code) );
  };

  const handleAddToCartPress = (recommendation: Recommendation) => {
    clientEventsService.createEvent(
      'shop.recommendation.add-to-cart-press', currentArticle.number);
    recommendation.session_id = storageService.getSessionId();
    const event = new CustomEvent('fpt-add-to-cart',
      { detail: recommendation as any });
    window.dispatchEvent(event);
    log.info('dispatched event', event);
    shopService.handleAddToCart(recommendation, currentArticle);
    clearModal();
  };

  const handleBackToShopPress = (matching: Matching) => {
    clearModal();
  };

  const handleRecommendationBackToShopPress = (recommendation: Recommendation) => {
    clientEventsService.createEvent('shop.recommendation.back-to-shop-press');
    clearModal();
    recommendation.session_id = storageService.getSessionId();
    const event = new CustomEvent('fpt-back-to-shop',
      { detail: recommendation as any });
    window.dispatchEvent(event);
    log.info(`dispatched event`, event);
  };

  const handleCancelPress = (recommendation: Recommendation) => {
    clientEventsService.createEvent('shop.recommendation.cancel-press');
    clearModal();
    recommendation.session_id = storageService.getSessionId();
    const event = new CustomEvent('fpt-cancel', { detail: recommendation as any });
    window.dispatchEvent(event);
    log.info('dispatched event', event);
  };

  const handlePiReconstructionRequest = () => {
    // clientEventsService.createEvent('shop.recommendation.cancel-press');
    // clearModal();
    log.info('displaying Email Form');
    displayEmailForm();
  };

  const handleQRPanelClose = () => {
    clearModal();
    sessionService.updateExitCode('PDP', 'qr.closed');
    clientEventsService.createEvent('shop.qr.closed');
  };

  const handleMeasureAgainPress = () => {
    clientEventsService.createEvent('shop.recommendation.measure-again-press');
    log.info('New Measurement Press');
    resetMeasurement();

    shopService.createSession()
      .then(result => {
        shopService.updateSessionId(result);
        const pageUrl = new URL(window.location.href);
        const { searchParams } = pageUrl;

        if (RenderUtil.isMobile() && searchParams.get('fpt-session')) {
          searchParams.set('fpt-session', result);
          searchParams.delete('fpt-measurement');
          window.location.replace(pageUrl);
          return;
        }

        if (RenderUtil.isMobile()) {
          displaySplashscreen();
          return;
        }

        displayDesktopWidget();
      })

  };

  const displayFeedbackConfirmDialog = (recommendation: Recommendation) => {
    setModalData({ recommendation });
    setModalView(ModalView.FEEDBACK_CONFIRM);
    RenderUtil.openModal();
  };

  const handleFeedbackConfirm = (outcome) => {
    const confirmation = new CustomEvent('fpt-feedback-confirm', {detail:outcome});
    window.dispatchEvent(confirmation);
    clearModal();
    if (outcome.confirm) {
      shopService.logRecommendationFeedback(outcome.recommendation);
    }
  }

  const processArticleData = (articleData: ArticleData, settings: ShopSettings) => {
    const { currency, lang, pricePattern } = settings;

    if (!articleData.currency) {
      articleData.currency = currency || 'EUR';
    }

    log.info(`using article price pattern ${pricePattern}`);
    log.info(`processing article price ${articleData.price}`);
    const priceNum = RenderUtil.parseArticlePrice(articleData.price, settings);

    const selectedLanguage = lang || language;

    articleData.sum = checkoutService.formatCurrency(
      priceNum, selectedLanguage, articleData.currency);

  };

  const params = new URL(window.location.href).searchParams;

  const handleMeasurementUrlParam = (settings:ShopSettings) => {
    const measurementId = params.get('fpt-measurement');
    sessionId = params.get('fpt-session');
    shopService.updateSessionId(sessionId);
    const articleNumber = params.get('fpt-article-number');

    if (!articleList || articleList.length === 0) {
      log.warn('article list is empty!');
    }

    const returnUrl = currentArticle?.returnUrl || settings.returnUrl;

    if (returnUrl && settings.recommendationEnabled == false && measurementId) {
      window.location.href = shopService.parseReturnUrl(returnUrl,
        { sessionId, measurementId },{ id:'', number: articleNumber, sizes: [] });
      return;
    }

    log.info(`looking for ${articleNumber} in article list:`, articleList);

    const articleData = articleList.find(
      item => item.number === articleNumber);

    if (articleData) {
      processArticleData(articleData, settings);
      setArticle(articleData);
      currentArticle = articleData;
    } else if (articleList.length === 1) {

      // FIXME: when coming back to shop from polling
      // in mobile if article number in the url is different
      // than the article number in data attribute then
      // an error was thrown. this was due to article variants
      // being different, but the article was the same. Since
      // the article list was implemented for multiwidget support
      // when there is only one article in the list we skip the
      // article number check to avoid the error. A more ideal
      // solution would be to handle different variants properly

      log.warn(`Discrepancy between article number in url and article number in data attribute detected`);
      log.warn(`PDP article: ${articleList[0].number} external article: ${articleNumber}`);

      // Found another Scenario where if sku data attribute is used instead
      // of article number. The algorithm falls into this block
      // beacause article number is an empty string and article number is
      // defined in the url
      if (!articleList[0].number && articleNumber ) articleList[0].number = articleNumber;
      processArticleData(articleList[0], settings);
      setArticle(articleList[0]);
      currentArticle = articleList[0];

    } else {
      const err = new Error(`unable to identify article ${articleNumber} on user redirect to shop`);
      displayError(err);
    }
  };

  const handleCustomerReferenceChange = (newRef:string) => {
    log.info(`[AppController] data attribute changed to ${newRef}`);
    customerReference = newRef;
  }

  const handleEmailFormSubmit = (email:string) => {
    log.info(`[AppController] Received email submit event`, email);
    clearModal();
    setModalView(ModalView.PROCESSING);
    RenderUtil.openModal();
    log.info(`[AppController] creating PI Reconstruction`);
    measurementService.createPiReconstruction(email)
      .then(result => {
        log.info(`[AppController] Got PI Reconstruction response`, result);
        displaySuccessOutcome();
      })
      .catch(err => {
        log.error(`something went wrong ${err.message}`);
        displayError(err);
      });
  }

  useEffect(() => {
    log.info('Loading shop settings . . .');
    window.addEventListener("fpt-secondary-matching", (e) => {
      log.info('Received secondary matching event', e);
    });
    shopSettingsService.getSettings()
      .then((settings:ShopSettings) => {
        log.info(`Got shop settings: ${settings}`);

        settings = settings || { domain: ''};
        settings.desktopFlowEnabled = 'desktopFlowEnabled' in settings ?
          settings.desktopFlowEnabled : true;

        const { lang, theme } = settings;
        const widgetLang = RenderUtil.getWidgetLang();

        if (widgetLang && widgetLang in i18nConfig) {
          // lang specified in data attribute and supported
          return handleLanguageSetting(widgetLang, settings)
            .then(() => settings);
        } else if (lang && lang in i18nConfig) {
          // lang specified in settings and supported

          return handleLanguageSetting(lang, settings)
            .then(() => settings);

        } else if (lang) {
          // lang specified in settings but not supported

          log.warn(`unsupported shop language setting: ${lang}`);

          const langOptions = {
            lang: language,
            i18nNamespace: settings.theme?.i18nNamespace,
          };

          return AssetsService.fetchLanguageFile(langOptions)
            .then( result => {
              log.info('setting language', language);
              i18nConfig[language].messages = result;
              setLocale(i18nConfig[language].locale);
              const m = RenderUtil.getTranslationMessages(language, i18nConfig);
              setMessages(m);
            })
            .then(() => settings);
        } else {
          // lang not specified in settings

          log.warn(`setting browser language: ${language}`);

          const langOptions = {
            lang: language,
            i18nNamespace: settings.theme?.i18nNamespace,
          };

          return AssetsService.fetchLanguageFile(langOptions)
            .then( result => {
              log.info('setting language', language);
              i18nConfig[language].messages = result;
              setLocale(i18nConfig[language].locale);
              const m = RenderUtil.getTranslationMessages(language, i18nConfig);
              setMessages(m);
            })
            .then(() => settings);
        }
    })
    .then((settings:typeof shopSettings) => {
      const { theme, steps } = settings;

      if (theme) {
        const parsedTheme = RenderUtil.parseTheme(theme);
        setCustomTheme(parsedTheme);
        storageService.update('themeName', parsedTheme.name);
      }

      if (settings.insoleAvailable) {
        settings.steps = steps ? `${steps}-PIOPT` : `DEFAULT-PIOPT`;
      }

      if (settings.abTesting) {
        const testFlows = settings.abTesting.split(',').map(item => item.trim());
        const optCount = testFlows.length;
        const idx = RenderUtil.getRandomInt(optCount);
        const test = testFlows[idx];
        settings.steps = settings.steps.replace(/^(\w+)/, test);
      }

      setShopSettings(settings);
      shopService.setShopSettings(settings);
      emitter.emit(WidgetEvents.onSettingsLoaded, settings);

      if (
        params.has('fpt-measurement') &&
        params.has('fpt-session') &&
        params.has('fpt-article-number')
      ) {
        handleMeasurementUrlParam(settings);
      } else if (
        params.has('fpt-session') &&
        params.has('fpt-article-number')
      ) {
        sessionId = params.get('fpt-session');
        shopService.updateSessionId(sessionId);
        setModalView(ModalView.PROCESSING);
        RenderUtil.openModal();
      }
    });
  }, []);

  useEffect(() => {
    if (!shopSettings.domain) {
      return;
    }

    if (emitter.listeners(CtaPanelEvents.onCtaPress).length > 0) {
      return;
    }

    log.info(`adding event listeners with shop Settings: ${shopSettings.domain}`);

    emitter.addListener(CtaPanelEvents.onCtaPress, handleCtaButtonPress);
    emitter.addListener(SplashscreenEvents.onFunnelSelect, handleFunnelSelect);
    emitter.addListener(SplashscreenEvents.onLanguageSelect, handleLanguageSelect);
    emitter.addListener(Events.onCancelPress, handleCancelPress);
    emitter.addListener(Events.onBackToShopPress, handleBackToShopPress);
    emitter.addListener(RecommendationEvents.onRecommendationBackToShopPress,
      handleRecommendationBackToShopPress);
    emitter.addListener(Events.onMeasureAgainPress, handleMeasureAgainPress);
    emitter.addListener(Events.onStartAgainPress, handleMeasureAgainPress);
    emitter.addListener(PollingEvents.onQRPanelClose, handleQRPanelClose);
    emitter.addListener(CtaPanelEvents.onRecommendationPress, handleRecommendationPress);
    emitter.addListener(RecommendationEvents.onAddToCartPress, handleAddToCartPress);
    emitter.addListener(RecommendationEvents.onPiReconstructionRequest, handlePiReconstructionRequest);
    emitter.addListener(ModalEvents.onCloseModalPress, clearModal);
    emitter.addListener(FaEvents.onSpeedOptionConfirm, handleSpeedOptionConfirm);
    emitter.addListener(FaEvents.onMeasureOptionConfirm, handleMeasureOptionConfirm);
    emitter.addListener(McEvents.onMcPollingStart, handleMcPollingStart);
    emitter.addListener(McEvents.onMcPollingDone, handleMcPollingDone);
    emitter.addListener(McEvents.onMcPollingCancelled, handleMcPollingCancelled);
    emitter.addListener(McEvents.onMcPollingError, handleMcPollingError);
    emitter.addListener(WidgetEvents.onProfileMeasurementSet, handleProfileMeasurementSet);
    emitter.addListener(WidgetEvents.onCustomerReferenceChange, handleCustomerReferenceChange);
    emitter.addListener(EmailFormEvents.onEmailSubmit, handleEmailFormSubmit);
    RenderUtil.observeForCustomerReferenceChange(emitter);

    if (headlessEnabled) {
      log.info('Hooking to fpt start event');
      window.addEventListener('fpt-start', handleFptStart);
      emitter.addListener(RecommendationEvents.onFeedbackConfirm, handleFeedbackConfirm);
      window.addEventListener('fpt-feedback-submit', (data:CustomEvent) => displayFeedbackConfirmDialog(data.detail));
    }

    const initEvent = new Event('fpt-initialized');
    window.dispatchEvent(initEvent);

    if (
      params.has('fpt-measurement') &&
      params.has('fpt-session') &&
      params.has('fpt-article-number')
    ) {
      const measurementId = params.get('fpt-measurement');
      sessionId = params.get('fpt-session');

      if (article && article.id === 'test') {
        return;
      }

      const event = new CustomEvent("fpt-measurement", {
        detail: { measurementId, sessionId }
      });

      window.dispatchEvent(event);

      shopService.handleMeasuringResult({
        result: {
          measurementId, sessionId,
        },
      }, article)
        .then((result:Matching) => {
          emitter.emit(WidgetEvents.onMatching, result);
          const event = new CustomEvent("fpt-matching", { detail: result });
          window.dispatchEvent(event);

          if (shopService.isSuccessfulMatching(result)) {
            displayRecommendation(result);
            shopService.createConversion(
              result,
              article,
              CONVERSION_ACTION.MATCHING_PRIMARY,
            );
            return;
          }

          throw new DefaultError(result.error_code, 'Matching failed');
        })
        .catch(err => {
          displayError(err);
        });
    } else if ( // Desktop flow scenario
      params.has('fpt-session') &&
      params.has('fpt-article-number')
    ) {
      console.warn(`[AppController] got session from url, should we display splash screen?`);
      sessionId = params.get('fpt-session');
      const articleNumber = params.get('fpt-article-number');

      if (!articleList || articleList.length === 0) {
        log.warn('article list is empty!');
      }

      let articleData = articleList.find(
       item => item.number === articleNumber);

      if (!articleData
          && articleList.length === 1
          && !articleList[0].number) {
        log.warn('article number in URL does not match articles in PDP!');
        articleData = articleList[0];
      }

      // When the article number from QR code
      // Does not match with article number in PDP
      if (!articleData
        && articleList.length > 0
        && articleList[0].number) {
        articleData = { number: articleNumber };
      }

      currentArticle = articleData;
      setArticle(articleData);

      log.info('Checking current article . . .', article);

      shopService.handleExistingSession(sessionId, articleData)
        .then((result:ArticleResource) => {
          // For unknown reason art
          if (result.article_number !== articleNumber) {
            const message = 'Could not reconcile article number in url with PDP';
            throw new Error(message);
          }

          // When the article data comes from QR code
          if (!articleData.name) {
            articleData.name = result.article_name;
            articleData.manufacturer = result.manufacturer;
            currentArticle = articleData;
            setArticle(articleData);
          }

          log.info('article number in URL reconciled with PDP article');
          return result;
        })
        .then(processArticleDataAndDisplaySplashscreen)
        .catch(handleProcessArticleDataError);
    }

    if (bannerId) {
      log.info(`has banner id: ${bannerId}`);
      const banner = document.querySelector(`#${bannerId}`) as HTMLElement;
      const art = shopService.getArticleData(banner);
      banner.addEventListener('click', () => handleCtaButtonPress(art));
    }



  }, [article, shopSettings, language, customTheme]);


  return (
    <IntlProvider
        locale={locale}
        defaultLocale={i18nConfig.en.locale}
        messages={messages}>
        <ShopSettingsContext.Provider value={shopSettings}>
          <ArticleContext.Provider value={article}>
            <ThemeProvider theme={customTheme}>
              <Modal view={modalView} emitter={emitter} data={modalData} />
            </ThemeProvider>
          </ArticleContext.Provider>
        </ShopSettingsContext.Provider>
      </IntlProvider>
  );
};
